import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt } from '@miq/utils';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../models/report.model';
import { LovValues, TemplateFieldsDisplayFn } from '../../../models/template.model';
import { getMediaInformation } from '../templates-formatter.const';
import { sortAttributesByFieldId } from '../utils/argument-sorting';
import {
  displayFnWrapper,
  formatExternalMeasurementFieldVal,
  getLovAttributesFormattedData,
  mapAndFormatDynamicValue,
  minMaxRangeFormattedValue,
} from '../utils/dynamic-data-retrievers';

export const displayFnMap: TemplateFieldsDisplayFn = {
  27: {
    displayFn: (record: ReportRecord, options, ...args) => {
      const finalArgs = sortAttributesByFieldId(options.locale, args, 27);
      const values = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(', ', ...values);
    },
    routeFn: ({ data: entityRecord }: ReportRecord) => {
      return ['/records', 'property', digger(entityRecord, 'entity.id'), 'details'];
    },
  },
  28: {
    displayFn: (record: ReportRecord, _, landlord) => {
      const landlordVal = mapAndFormatDynamicValue(record, landlord);
      return Array.isArray(landlordVal) ? concatValuesBy(', ', ...landlordVal) : landlordVal;
    },
  },
  29: {
    displayFn: (record: ReportRecord, _, ownership) => {
      const result = getLovAttributesFormattedData(record, ownership);
      return result;
    },
  },
  30: {
    displayFn: (record: ReportRecord, options, ...args) => {
      const finalArgs = sortAttributesByFieldId(options.locale, args, 30);
      const completionYearValues = finalArgs.map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy(',', ...completionYearValues);
    },
  },
  31: {
    displayFn: displayFnWrapper,
  },
  32: {
    displayFn: displayFnWrapper,
  },
  33: {
    displayFn: (record: ReportRecord, _options, arg1, maxTypicalFloorPlate) => {
      const minTypicalFloorPlate = { ...arg1, uomId: null, uomArgument: null };
      return minMaxRangeFormattedValue(record, minTypicalFloorPlate, maxTypicalFloorPlate);
    },
  },
  34: {
    /*
     * here 34('Type of A/C System') is attribute id which is defined in DB for
     * every attribute('Type of A/C System', 'Landlord', 'Location',
     * 'Property Name' etc...).
     * From service we are receiving display arguments has array for every
     * attribute(it might have one or more).
     * To get the attribute value here we are passing @{arg1, arg2} which
     * contains attribute skey, uom skey(optional) and entity info.
     * Data coming from template api that belongs to the user and report.
     *  "34": {
     *    "id": 34,
     *    "label": "地上总层数",
     *    "displayFnArguments": [
     *      {
     *        "id": 910,
     *        "entity": "property"
     *      },
     *      {
     *        "id": 16620,
     *        "entity": "property"
     *      }
     *    ]
     *  },
     * The above one example for attribute info. Overall we know the field
     * contains two arguments but context is unknown.
     * Therefore function arguments names are generic.
     */
    displayFn: (record: ReportRecord, _options, arg1, arg2) => {
      return concatValuesBy('; ', mapAndFormatDynamicValue(record, arg1), mapAndFormatDynamicValue(record, arg2));
    },
  },
  35: {
    displayFn: displayFnWrapper,
  },
  36: {
    displayFn: (record: ReportRecord, _options, weekDaysStart, weekDaysEnd) => {
      const weekDays = `${concatValuesBy(
        ' - ',
        mapAndFormatDynamicValue(record, weekDaysStart),
        mapAndFormatDynamicValue(record, weekDaysEnd)
      )}`;

      return weekDays;
    },
  },
  37: {
    displayFn: (record: ReportRecord, _, water) => {
      const result = getLovAttributesFormattedData(record, water);
      return result;
    },
  },
  38: {
    displayFn: displayFnWrapper,
  },
  39: {
    displayFn: (record: ReportRecord, _options, ceiling) => {
      ceiling = {
        ...ceiling,
        dataFormattingDesc: 'ceiling',
      };
      const ceilingValue = formatExternalMeasurementFieldVal(record, ceiling, 'property_measurement_size');
      return ceilingValue;
    },
  },
  40: {
    displayFn: (record: ReportRecord, _options, raisedFloor) => {
      raisedFloor = {
        ...raisedFloor,
        dataFormattingDesc: 'raisedflrdepth',
      };
      const raisedFloorValue = formatExternalMeasurementFieldVal(record, raisedFloor, 'property_measurement_size');
      return raisedFloorValue;
    },
  },
  41: {
    displayFn: displayFnWrapper,
  },
  42: {
    displayFn: (record: ReportRecord, _options, carParkingFeeLot, carParkingFeeMonth) => {
      const val1 = mapAndFormatDynamicValue(record, carParkingFeeLot);
      const val2 = mapAndFormatDynamicValue(record, carParkingFeeMonth);
      return concatValuesBy(' / ', val1, val2);
    },
  },
  43: {
    displayFn: (record: ReportRecord, _, management) => {
      const managementVal = mapAndFormatDynamicValue(record, management);
      return Array.isArray(managementVal) ? concatValuesBy(', ', ...managementVal) : managementVal;
    },
  },
  44: {
    displayFn: (record: ReportRecord, _options, metroStation, walkingDistance, metroLine) => {
      // order of fields requested in MIQ-7646
      const metroValues = [walkingDistance, metroLine, metroStation].map(arg => mapAndFormatDynamicValue(record, arg));
      return concatValuesBy('; ', ...metroValues);
    },
  },
  45: {
    displayFn: (record: ReportRecord, _, power, powerSystem) => {
      return power.uomArgument.lovValues.pipe(
        map((uomLov: LovValues[]) => {
          const powerVal = mapAndFormatDynamicValue(record, power, uomLov);
          const powerSystemVal = mapAndFormatDynamicValue(record, powerSystem);
          const result = concatValuesBy(' - ', powerVal, powerSystemVal);
          return result;
        })
      );
    },
  },
  46: {
    displayFn: (record: ReportRecord, _options, depth) => {
      depth = {
        ...depth,
        dataFormattingDesc: 'depthfrmcoretowindow',
      };
      const depthValue = formatExternalMeasurementFieldVal(record, depth, 'property_measurement_size');
      return depthValue;
    },
  },
  47: {
    displayFn: displayFnWrapper,
  },
  48: {
    displayFn: displayFnWrapper,
  },
  49: {
    displayFn: (record: ReportRecord, _options, arg1, arg2) => {
      const values = concatValuesBy(
        defaultTxt,
        mapAndFormatDynamicValue(record, arg1),
        mapAndFormatDynamicValue(record, arg2)
      );
      return values;
    },
  },
  125: {
    displayFn: displayFnWrapper,
  },
  126: {
    displayFn: displayFnWrapper,
  },
  127: {
    displayFn: displayFnWrapper,
  },
  98: {
    // 98 - Primary Image
    displayFn: (record: ReportRecord) => {
      return record.associatedProperty
        ? record.associatedProperty.getMediaInformation()
        : record.data.getMediaInformation();
    },
  },
  99: {
    // 99 - Additional Image
    displayFn: (record: ReportRecord) => {
      return record.associatedProperty
        ? record.associatedProperty.getMediaInformation()
        : record.data.getMediaInformation();
    },
  },
  347: {
    // 347 - Floor Plan Images
    displayFn: (record: ReportRecord) => {
      return getMediaInformation(record);
    },
  },
  335: {
    // 335 - Floor and Suite info
    displayFn: (record: ReportRecord, _, suiteArg, floorArg) => {
      const floorValue = mapAndFormatDynamicValue(record, floorArg);
      const suiteValue = mapAndFormatDynamicValue(record, suiteArg);
      return `${floorValue} | ${suiteValue}`;
    },
  },
};
